import { Icon, type IconSymbol } from "@spz/ui/components/icon";
import {
  type ComponentProps,
  type FocusEvent,
  useEffect,
  useId,
  useState,
} from "react";
import type { FieldError } from "react-hook-form";
import { cn } from "../lib/utils";
import { InlineFeedback, InlineFeedbackGroup } from "./inline-feedback";

export type TextFieldProps = ComponentProps<"input"> & {
  helperText?: string;
  leftIcon?: IconSymbol;
  rightIcon?: IconSymbol;
  rightIconClick?: () => void;
  floatingPlaceholder?: boolean;
  error?: FieldError;
};

interface TextFieldComponentProps extends TextFieldProps {
  ref?: React.Ref<HTMLInputElement>;
}

export const TextField: React.FC<TextFieldComponentProps> = ({
  className,
  helperText,
  leftIcon,
  rightIcon,
  rightIconClick,
  floatingPlaceholder = false,
  placeholder,
  id,
  type,
  error,
  disabled,
  value,
  defaultValue,
  ref,
  ...restProps
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(Boolean(value || defaultValue));

  const inputPlaceholder = floatingPlaceholder ? undefined : placeholder;
  const inputId = id || useId();

  // Effect to handle controlled component value changes
  useEffect(() => {
    setHasValue(Boolean(value));
  }, [value]);

  const handleFocus = () => setIsFocused(true);

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    setHasValue(!!e.target.value);
    restProps.onBlur?.(e);
  };

  // Handle onChange to update hasValue for uncontrolled components
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasValue(!!e.target.value);
    // Forward the onChange event if provided
    restProps.onChange?.(e);
  };

  return (
    <div className="flex flex-col space-y-1">
      <div
        className={cn(
          "relative flex items-center border rounded bg-white transition-colors",
          error ? "border-red-500" : "border-gray-200",
          !disabled &&
            !error &&
            "hover:border-pink-200 focus-within:border-pink-300",
          disabled
            ? [
                "bg-gray-50 text-gray-700 placeholder:text-gray-500 border-gray-200",
                "cursor-not-allowed",
              ]
            : [],
          className,
        )}
      >
        {!floatingPlaceholder && leftIcon && (
          <div className="flex items-center ml-2 text-gray-500">
            <Icon icon={leftIcon} />
          </div>
        )}

        <div className="relative flex-1">
          {floatingPlaceholder && placeholder && (
            <label
              htmlFor={inputId}
              className={cn(
                "absolute top-1/2 -translate-y-1/2 text-gray-700 transition-all pointer-events-none",
                (isFocused || hasValue) && "!text-caption top-4",
                "left-4",
              )}
            >
              {placeholder}
            </label>
          )}

          <input
            ref={ref}
            id={inputId}
            type={type || "text"}
            placeholder={inputPlaceholder}
            disabled={disabled}
            value={value}
            defaultValue={defaultValue}
            className={cn(
              "w-full rounded !text-caption py-2",
              "placeholder:text-gray-700 focus-visible:outline-none",
              disabled
                ? "opacity-50 cursor-not-allowed"
                : "focus-visible:outline-none",
              floatingPlaceholder ? "pl-4" : leftIcon ? "px-2" : "px-4",
              floatingPlaceholder && "pt-[22px] font-title",
              className,
            )}
            {...restProps}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </div>

        {rightIcon && (
          <div
            className={cn(
              "flex items-center pr-2 text-gray-500",
              rightIconClick && "cursor-pointer",
            )}
            onClick={rightIconClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                rightIconClick?.();
              }
            }}
          >
            <Icon icon={rightIcon} />
          </div>
        )}
      </div>

      {(error?.message || helperText) && (
        <InlineFeedbackGroup>
          {error?.message && (
            <InlineFeedback variant="critical" message={error.message} nested />
          )}
          {helperText && (
            <InlineFeedback variant="info" message={helperText} nested />
          )}
        </InlineFeedbackGroup>
      )}
    </div>
  );
};
